import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaIdCard, FaPlayCircle, FaTrashAlt, FaUserCircle } from "react-icons/fa";
import { createPortal } from "react-dom";
import {
  cancelAgentEmailChange,
  getMyAgentProfile,
  isAuthenticated,
  requestAgentEmailChange,
  updateMyAgentProfile
} from "../auth";
import { elakay_API } from "../../config";
import ThirdLevelCategorySelector from "../pageInside/ThirdLevelCategorySelector";
import CountryZipLocationFields from "../pageInside/CountryZipLocationFields";
import LanguageSelector from "../pageInside/LanguageSelector";
import ProfessionalVideoPlayer from "../pageInside/ProfessionalVideoPlayer";
import {
  analyzeProfessionalVideoUrl,
  PROFESSIONAL_VIDEO_LANGUAGES,
  PROFESSIONAL_VIDEO_LANGUAGE_NAMES
} from "../pageInside/professionalVideo";
import "./AgentProfileManager.css";
import { elakayAutoText } from "../pageInside/dataElakay";
import { prepareUpload } from "../../utils/uploadPolicy";
import { readJsonOrUploadError } from "../../utils/uploadResponse";


const EMPTY = {
  firstname: "",
  lastname: "",
  displayName: "",
  biography: "",
  experience: "",
  professionalVideos: {
    en: { url: "" }, es: { url: "" }, ht: { url: "" }, fr: { url: "" }, pt: { url: "" }, defaultLanguage: ""
  },
  publicPhone: "",
  showPhone: false,
  visible: true,
  categoryPublicIds: [],
  languages: [],
  location: { country: "", state: "", city: "", zipCode: "" }
};

const remainingText = (effectiveAt, expiredLabel) => {
  const milliseconds = new Date(effectiveAt).getTime() - Date.now();
  if (milliseconds <= 0) return expiredLabel;
  const days = Math.floor(milliseconds / 86400000);
  const hours = Math.floor((milliseconds % 86400000) / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  return `${days}d ${hours}h ${minutes}m`;
};

const AgentProfileManager = ({ infoSource, onSaved, onManagePhone }) => {
  const { token } = isAuthenticated();
  const tr = useCallback((key, fallback) =>
    infoSource?.laguangeElakay?.("agentProfile", infoSource.languageAPI)?.[key] || fallback,
  [infoSource]);
  const [form, setForm] = useState(EMPTY);
  const [account, setAccount] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [remaining, setRemaining] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [triggerTarget, setTriggerTarget] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const savedPhones = Array.isArray(infoSource?.phoneUser)
    ? infoSource.phoneUser.filter((phone) => phone?.International)
    : [];
  const currentPhoneIsSaved = savedPhones.some(
    (phone) => phone.International === form.publicPhone
  );

  useEffect(() => {
    const findTarget = () => setTriggerTarget(document.getElementById("agent-profile-trigger-slot"));
    findTarget();
    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getMyAgentProfile(token);
      setAccount(data);
      const profile = data.profile || {};
      setForm({
        firstname: data.firstname || "",
        lastname: data.lastname || "",
        displayName: profile.displayName || "",
        biography: profile.biography || "",
        experience: profile.experience || "",
        professionalVideos: {
          ...Object.fromEntries(PROFESSIONAL_VIDEO_LANGUAGES.map((language) => [language, {
            url: profile.professionalVideos?.[language]?.url || ""
          }])),
          defaultLanguage: profile.professionalVideos?.defaultLanguage || ""
        },
        publicPhone: profile.publicPhone || "",
        showPhone: profile.showPhone === true,
        visible: profile.visible !== false,
        categoryPublicIds: (data.categories || []).map((category) => category.publicId),
        languages: Array.isArray(profile.languages) ? profile.languages : [],
        location: {
          country: profile.location?.country || "",
          state: profile.location?.state || "",
          city: profile.location?.city || "",
          zipCode: profile.location?.zipCode || ""
        }
      });
    } catch (requestError) {
      if (!requestError.message.includes("only to Elakay Agents")) setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview("");
      return undefined;
    }
    const previewUrl = URL.createObjectURL(avatarFile);
    setAvatarPreview(previewUrl);
    setAvatarLoadFailed(false);
    return () => URL.revokeObjectURL(previewUrl);
  }, [avatarFile]);

  useEffect(() => {
    if (!account) return;
    const timer = setInterval(() => {
      const pending = account.pendingEmailChange;
      setRemaining(
        pending?.status === "pending"
          ? remainingText(pending.effectiveAt, tr("processing", "Processing…"))
          : ""
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [account, tr]);

  const field = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const setVideoUrl = (language, url) => {
    setForm((current) => {
      const professionalVideos = {
        ...current.professionalVideos,
        [language]: { url }
      };
      const available = PROFESSIONAL_VIDEO_LANGUAGES.filter((code) => professionalVideos[code]?.url?.trim());
      if (available.length === 1) professionalVideos.defaultLanguage = available[0];
      else if (!available.includes(professionalVideos.defaultLanguage)) professionalVideos.defaultLanguage = available[0] || "";
      return { ...current, professionalVideos };
    });
    if (url.trim()) setPreviewLanguage(language);
  };
  const setDefaultVideo = (language) => setForm((current) => ({
    ...current,
    professionalVideos: { ...current.professionalVideos, defaultLanguage: language }
  }));

  const uploadAvatar = async () => {
    if (!avatarFile) return;
    const body = new FormData();
    body.append("photos", await prepareUpload("shopImage", avatarFile));
    body.append("n_0", "0");
    body.append("w_0", "700");
    body.append("h_0", "700");
    const response = await fetch(`${elakay_API}/addUserImgPud`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body
    });
    const data = await readJsonOrUploadError(response);
    if (!response.ok || data.success === false) throw new Error(data.error || data.errors?.[0]?.message || tr("avatarError", "Avatar upload failed."));
    setAvatarVersion(Date.now());
    setAvatarLoadFailed(false);
    setAvatarFile(null);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const invalidLanguage = PROFESSIONAL_VIDEO_LANGUAGES.find((language) => {
        const url = form.professionalVideos?.[language]?.url;
        return url?.trim() && !analyzeProfessionalVideoUrl(url)?.valid;
      });
      if (invalidLanguage) throw new Error(tr("invalidVideoUrl", "Enter a valid secure HTTPS video URL."));
      await uploadAvatar();
      await updateMyAgentProfile(token, form);
      setMessage(tr(elakayAutoText("h_c75c31dffb082c", "saved"), elakayAutoText("h_dd3c7028ca02b5", "Agent profile saved.")));
      await load();
      onSaved?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const requestEmail = async () => {
    setError("");
    try {
      const data = await requestAgentEmailChange(token, pendingEmail);
      setAccount((current) => ({ ...current, pendingEmailChange: data.pendingEmailChange }));
      setPendingEmail("");
      setMessage(tr("emailScheduled", elakayAutoText("h_9667cd0889e0f4", "Email change scheduled for 7 days from now.")));
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const cancelEmail = async () => {
    try {
      const data = await cancelAgentEmailChange(token);
      setAccount((current) => ({ ...current, pendingEmailChange: data.pendingEmailChange }));
      setMessage(tr("emailCancelled", elakayAutoText("h_29b74ef854c43f", "Email change cancelled.")));
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  if (loading) return <div className="agent-profile-loading">{tr("loading", "Loading Agent Profile…")}</div>;
  if (!account) return null;

  const pending = account.pendingEmailChange;
  const avatarUrl = `${elakay_API}/user_avatar/${isAuthenticated().a}?v=${avatarVersion}`;

  return (
    <>
      {triggerTarget && createPortal(<button
        type="button"
        className="agent-profile-modal-trigger"
        onClick={() => setShowProfileModal(true)}
        aria-label={tr("openProfessionalProfile", "Open professional public profile")}
      >
        <FaIdCard aria-hidden="true" />
        <span>
          <strong>{tr("professionalProfile", "Professional public profile")}</strong>
          <small>{tr("openProfileManager", "Open Agent Profile management")}</small>
        </span>
      </button>, triggerTarget)}

      <Modal
        size="xl"
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        dialogClassName="agent-profile-modal"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaIdCard aria-hidden="true" />{" "}
            {tr("professionalProfile", "Professional public profile")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
    <section className="agent-profile-manager">
      <div className="agent-profile-hero">
        <div>
          <span className="agent-profile-eyebrow">{tr("professionalProfile", "Professional public profile")}</span>
          <h2>{tr("managementTitle", "Agent Profile Management")}</h2>
          <p>{tr("managementHelp", "This is the single source of truth used when Sellers and Providers choose an Agent.")}</p>
        </div>
        <span className={`agent-profile-status ${account.profile?.status || "draft"}`}>{tr(account.profile?.status || "draft", account.profile?.status || "draft")}</span>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form onSubmit={save}>
        <div className="agent-profile-grid">
          <div className="agent-profile-panel avatar-panel">
            <div className="agent-avatar-preview">
              {avatarLoadFailed ? (
                <FaUserCircle aria-label={tr("avatar", "Agent avatar")} />
              ) : (
                <img
                  src={avatarPreview || avatarUrl}
                  alt={tr("avatar", "Agent avatar")}
                  onLoad={() => setAvatarLoadFailed(false)}
                  onError={() => setAvatarLoadFailed(true)}
                />
              )}
            </div>
            <label className="agent-upload-button">
              {tr("changeAvatar", "Change avatar")}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={(event) => setAvatarFile(event.target.files?.[0] || null)} />
            </label>
            <small>{tr("avatarHint", "Square image, maximum 1 MB.")}</small>
          </div>

          <div className="agent-profile-panel identity-panel">
            <h3>{tr("identity", "Identity and presentation")}</h3>
            <div className="agent-fields two">
              <label>{tr("firstName", "First name")}<input value={form.firstname} onChange={(e) => field("firstname", e.target.value)} required /></label>
              <label>{tr("lastName", "Last name")}<input value={form.lastname} onChange={(e) => field("lastname", e.target.value)} required /></label>
            </div>
            <label>{tr("displayName", "Public display name")}<input value={form.displayName} onChange={(e) => field("displayName", e.target.value)} maxLength={100} /></label>
            <label>{tr("biography", "About Me")}<textarea value={form.biography} onChange={(e) => field("biography", e.target.value)} maxLength={3000} rows={7} placeholder={tr("biographyPlaceholder", "Explain your experience, specialties, services and why Sellers or Providers should choose you.")} /></label>
            <label>{tr("experience", "Experience")}<textarea value={form.experience} onChange={(e) => field("experience", e.target.value)} maxLength={1500} rows={4} /></label>
          </div>
        </div>

        <div className="agent-profile-panel agent-video-panel">
          <div className="agent-video-panel__heading">
            <div>
              <h3><FaPlayCircle aria-hidden="true" /> {tr("professionalIntroductionVideos", "Professional Introduction Videos")}</h3>
              <p>{tr("videoHelp", "Add up to one external presentation video for each Elakay language.")}</p>
            </div>
            <span>{PROFESSIONAL_VIDEO_LANGUAGES.filter((language) => form.professionalVideos?.[language]?.url?.trim()).length}/5</span>
          </div>
          <div className="agent-video-fields">
            {PROFESSIONAL_VIDEO_LANGUAGES.map((language) => {
              const url = form.professionalVideos?.[language]?.url || "";
              const analysis = analyzeProfessionalVideoUrl(url);
              const isDefault = form.professionalVideos?.defaultLanguage === language;
              return (
                <div className={`agent-video-field${url ? " configured" : ""}`} key={language}>
                  <div className="agent-video-field__label">
                    <label htmlFor={`agent-video-${language}`}>
                      <strong>{language.toUpperCase()}</strong> {PROFESSIONAL_VIDEO_LANGUAGE_NAMES[language]}
                    </label>
                    {analysis?.valid && <span>{analysis.platform}</span>}
                  </div>
                  <div className="agent-video-field__input">
                    <input
                      id={`agent-video-${language}`}
                      type="url"
                      inputMode="url"
                      value={url}
                      maxLength={2048}
                      placeholder={tr("videoUrl", "Video URL")}
                      aria-invalid={Boolean(url && !analysis?.valid)}
                      onChange={(event) => setVideoUrl(language, event.target.value)}
                    />
                    {url && (
                      <button type="button" className="agent-video-remove" onClick={() => setVideoUrl(language, "")} aria-label={`${tr("removeVideo", "Remove Video")} ${language.toUpperCase()}`}>
                        <FaTrashAlt aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {url && !analysis?.valid && <small className="agent-video-error">{tr("invalidVideoUrl", "Enter a valid secure HTTPS video URL.")}</small>}
                  {url && analysis?.valid && (
                    <div className="agent-video-field__actions">
                      <button type="button" onClick={() => setPreviewLanguage(language)}>{tr("previewVideo", "Preview Video")}</button>
                      <label className="agent-video-default">
                        <input type="radio" name="defaultProfessionalVideo" checked={isDefault} onChange={() => setDefaultVideo(language)} />
                        {isDefault ? tr("defaultVideo", "Default Video") : tr("setAsDefault", "Set as Default")}
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {PROFESSIONAL_VIDEO_LANGUAGES.some((language) => form.professionalVideos?.[language]?.url?.trim() && analyzeProfessionalVideoUrl(form.professionalVideos[language].url)?.valid) && (
            <div className="agent-video-preview">
              <h4>{tr("previewVideo", "Preview Video")}</h4>
              <ProfessionalVideoPlayer
                professionalVideos={form.professionalVideos}
                tr={tr}
                title={false}
                activeLanguage={previewLanguage || form.professionalVideos.defaultLanguage}
              />
            </div>
          )}
        </div>

        <div className="agent-profile-panel">
          <h3>{tr("location", "Location")}</h3>
          <CountryZipLocationFields
            value={form.location}
            onChange={(location) => field("location", location)}
            infoSource={infoSource}
          />
          <div className="agent-fields phone">
            <label>
              {tr("phone", "Phone number")}
              <select value={form.publicPhone || ""} onChange={(e) => field("publicPhone", e.target.value)}>
                <option value="">{tr("selectSavedPhone", "Select a saved phone number")}</option>
                {form.publicPhone && !currentPhoneIsSaved && (
                  <option value={form.publicPhone}>{form.publicPhone}</option>
                )}
                {savedPhones.map((phone, index) => (
                  <option
                    key={phone._id || phone.publicId || `${phone.International}-${index}`}
                    value={phone.International}
                  >
                    {phone.International}
                    {phone.default === true ? ` — ${tr("defaultPhone", "Default")}` : ""}
                  </option>
                ))}
              </select>
              <small>
                {savedPhones.length > 0
                  ? tr("savedPhoneHelp", "Choose a phone number already saved in your account.")
                  : tr("noSavedPhones", "No saved phone number is available yet.")}
              </small>
              {onManagePhone && (
                <button
                  type="button"
                  className="btn btn-link agent-manage-phone"
                  onClick={() => {
                    setShowProfileModal(false);
                    onManagePhone();
                  }}
                >
                  {tr("managePhones", "Manage saved phone numbers")}
                </button>
              )}
            </label>
            <label className={`agent-switch ${form.showPhone ? "selected" : ""}`}>
              <input type="checkbox" checked={form.showPhone} onChange={(e) => field("showPhone", e.target.checked)} />
              {tr("showPhone", "Show phone publicly")}
            </label>
            <label className={`agent-switch ${form.visible ? "selected" : ""}`}>
              <input type="checkbox" checked={form.visible} onChange={(e) => field("visible", e.target.checked)} />
              {tr("profileVisible", "Public profile visible")}
            </label>
          </div>
        </div>

        <div className="agent-profile-panel">
          <ThirdLevelCategorySelector
            token={token}
            infoSource={infoSource}
            selectedCategoryIds={form.categoryPublicIds}
            onSelectionChange={(ids) => field("categoryPublicIds", ids)}
            mode="agent-profile"
          />
        </div>

        <div className="agent-profile-panel">
          <LanguageSelector
            infoSource={infoSource}
            selectedCodes={form.languages}
            onChange={(languages) => field("languages", languages)}
            title={tr("languagesSpoken", "Languages Spoken")}
            help={tr("languagesSpokenHelp", "Select every language you speak.")}
          />
        </div>

        <button className="btn btn-primary agent-save" disabled={saving}>{saving ? tr("saving", "Saving…") : tr("saveProfile", "Save Agent Profile")}</button>
      </form>

      <div className="agent-profile-panel agent-email-panel">
        <h3>{tr("emailSecurity", "Secure email replacement")}</h3>
        <div className="agent-email-current"><span>{tr("currentEmail", "Current email")}</span><strong>{account.email}</strong></div>
        {pending?.status === "pending" ? (
          <div className="agent-email-pending">
            <div><span>{tr("pendingEmail", "Pending email")}</span><strong>{pending.email}</strong></div>
            <div><span>{tr("remainingTime", "Remaining time")}</span><strong>{remaining}</strong></div>
            <div><span>{tr("status", "Status")}</span><strong>{tr("pending", "Pending")}</strong></div>
            <button type="button" className="btn btn-outline-danger" onClick={cancelEmail}>{tr("cancelEmailChange", "Cancel email change")}</button>
          </div>
        ) : (
          <div className="agent-email-request">
            <input type="email" value={pendingEmail} onChange={(e) => setPendingEmail(e.target.value)} placeholder={tr("newEmail", "New email address")} />
            <button type="button" className="btn btn-outline-primary" disabled={!pendingEmail} onClick={requestEmail}>{tr("scheduleEmail", "Schedule email change")}</button>
          </div>
        )}
        <small>{tr("emailDelayHelp", "Your current email remains active for 7 days. You can cancel before the countdown ends.")}</small>
      </div>
    </section>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AgentProfileManager;
